import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FaTaxi, FaLock, FaUser, FaEye, FaEyeSlash } from "react-icons/fa";
import { loginDriver, reset } from "../../features/driver/driverAuthSlice";
import ChangePassword from '../../components/ChangePassword';
import { getFcmToken } from '../../firebase/getFcmToken';

const DriverLogin = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const { email, password } = formData;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { driver, isLoading, isError, isSuccess, isPendingApproval, message } =
    useSelector((state) => state.driverAuth);

  // Only redirect if driver is properly authenticated (not just from localStorage)
  useEffect(() => {
    if (driver && driver._id && isSuccess) {
      console.log("Driver login successful, redirecting to dashboard");
      navigate("/driver/dashboard", { replace: true });
    }
  }, [driver, navigate, isSuccess]);

  useEffect(() => {
    if (isError && message) {
      toast.error(message);
      dispatch(reset());
    }

    if (isPendingApproval && message) {
      toast.warning(message);
      dispatch(reset());
    }

    // Navigation is now handled in the first useEffect to avoid double redirect
  }, [isError, isSuccess, isPendingApproval, message, driver, navigate, dispatch]);

  const onChange = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // Enhanced validation
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }

    if (!email.includes('@') || email.length < 5) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (password.length < 3) {
      toast.error('Password must be at least 3 characters long');
      return;
    }

    // Trim whitespace to prevent issues
    // Try to obtain FCM token
    let fcmToken = null;
    try {
      fcmToken = await getFcmToken();
    } catch (err) {
      console.warn('Failed to get FCM token for driver login:', err);
    }

    const userData = {
      email: email.trim().toLowerCase(),
      password: password.trim(),
      fcmToken,
    };

    console.log('Attempting driver login with validated data');

    // Dispatch login - navigation will be handled by useEffect
    dispatch(loginDriver(userData));
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 page-container">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-primary flex items-center justify-center">
            <FaTaxi className="text-white text-3xl" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Driver Login
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Log in to your driver account to manage your rides
          </p>
        </div>

        {isPendingApproval && (
          <div className="my-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Account Pending Approval
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Your driver account is currently pending administrator
                    approval. Once approved, you'll be able to log in and start
                    accepting rides. This usually takes 24-48 hours.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={onSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div className="mb-4">
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaUser className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={onChange}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                  placeholder="Driver email address"
                  minLength={5}
                  maxLength={100}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={onChange}
                  className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                  placeholder="Driver password"
                  minLength={3}
                  maxLength={50}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showPassword ? (
                    <FaEyeSlash className="h-5 w-5" />
                  ) : (
                    <FaEye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Logging in...
                </span>
              ) : (
                "Sign In"
              )}
            </button>
          </div>
        </form>

        <div className="text-sm text-center mt-4">
          <Link to="/driver/forgot-password/send-otp" className="font-medium text-primary hover:text-primary-dark">
            Forgot your password?
          </Link>
        </div>

        <div className="mt-6 text-center">
          <div className="mb-4 p-4 bg-blue-50 rounded-md">
            <h3 className="font-medium text-blue-800 mb-1">
              How to become a driver
            </h3>
            <p className="text-sm text-blue-700">
              You can register as a driver online by filling out our application
              form. Simply submit your details and upload the required
              documents. Our team will review your application and activate your
              account once verified.
            </p>
            <div className="mt-3">
              <Link
                to="/driver/register"
                className="inline-block py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Register as a Driver
              </Link>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-4">
            <p className="text-sm text-gray-600 mb-2">
              Not a driver? Return to regular login
            </p>
            <Link
              to="/login"
              className="inline-block py-2 px-4 border border-primary text-primary rounded-md hover:bg-primary hover:text-white transition-colors"
            >
              Back to User Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverLogin;
